import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { APP_STORE_URLS, DEEP_LINK_SCHEME, APP_OPEN_TIMEOUT } from '../config/appStores';
import { apiService } from '../services/api';
import './PlacePage.css';

const PlacePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [placeData, setPlaceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceType, setDeviceType] = useState('unknown');

  useEffect(() => {
    // Detectar tipo de dispositivo
    const userAgent = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(userAgent)) {
      setDeviceType('ios');
    } else if (/android/.test(userAgent)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }

    // Buscar dados reais do estabelecimento
    const fetchPlaceData = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await apiService.getPlaceById(id);
        setPlaceData(data);
      } catch (err) {
        console.error('Erro ao buscar dados do estabelecimento:', err);
        setError(err.message || 'Erro ao carregar dados do estabelecimento');
      } finally {
        setLoading(false);
      }
    };

    fetchPlaceData();
  }, [id]);

  // const handleAppDownload = () => {
  //   if (deviceType === 'ios') {
  //     window.location.href = APP_STORE_URLS.ios;
  //   } else if (deviceType === 'android') {
  //     window.location.href = APP_STORE_URLS.android;
  //   } else {
  //     // Para desktop, mostrar opções
  //     setShowDownloadOptions(true);
  //   }
  // };

  const handleTryApp = () => {
    // Tentar abrir o app via deep link
    const appUrl = `${DEEP_LINK_SCHEME}://place/${id}`;
    const fallbackUrl = deviceType === 'ios' 
      ? APP_STORE_URLS.ios
      : APP_STORE_URLS.android;
    
    // Tentar abrir o app
    window.location.href = appUrl;
    
    // Se não conseguir abrir o app em 2 segundos, redirecionar para a store
    setTimeout(() => {
      window.location.href = fallbackUrl;
    }, APP_OPEN_TIMEOUT);
  };

  // Função para formatar o horário de funcionamento
  const formatBusinessHours = (businessHours) => {
    if (!businessHours) {
      return <div className="hours-group">Horário não informado</div>;
    }

    // Verificar se tem a estrutura hoursByDay
    if (businessHours.hoursByDay) {
      const daysOfWeek = {
        '0': 'Domingo',
        '1': 'Segunda-feira',
        '2': 'Terça-feira',
        '3': 'Quarta-feira',
        '4': 'Quinta-feira',
        '5': 'Sexta-feira',
        '6': 'Sábado'
      };
      const hoursList = [];

      // Processar cada dia da semana (0 = domingo, 1 = segunda, ..., 6 = sábado)
      for (let dayNum = 0; dayNum <= 6; dayNum++) {
        const dayKey = dayNum.toString();
        const dayHours = businessHours.hoursByDay[dayKey];
        
        if (dayHours && Array.isArray(dayHours) && dayHours.length > 0) {
          // Filtrar apenas horários ativos
          const activeHours = dayHours.filter(h => h.isActive);
          
          if (activeHours.length > 0) {
            // Agrupar horários do mesmo dia
            const hoursText = activeHours.map(h => `${h.openTime} - ${h.closeTime}`).join(' e ');
            hoursList.push({
              dayNum: dayNum,
              day: daysOfWeek[dayKey],
              hours: hoursText
            });
          }
        }
      }

      if (hoursList.length === 0) {
        return <div className="hours-group">Horário não informado</div>;
      }

      // Agrupar dias consecutivos com o mesmo horário
      const groupedHours = [];
      let currentGroup = null;

      hoursList.forEach((item, index) => {
        if (currentGroup && currentGroup.hours === item.hours && 
            item.dayNum === currentGroup.lastDayNum + 1) {
          // Adicionar ao grupo atual (dias consecutivos)
          currentGroup.days.push(item.day);
          currentGroup.lastDayNum = item.dayNum;
        } else {
          // Criar novo grupo
          if (currentGroup) {
            groupedHours.push(currentGroup);
          }
          currentGroup = {
            days: [item.day],
            hours: item.hours,
            lastDayNum: item.dayNum
          };
        }

        // Se for o último item, adicionar o grupo atual
        if (index === hoursList.length - 1) {
          groupedHours.push(currentGroup);
        }
      });

      return groupedHours.map((group, index) => {
        let daysLabel;
        if (group.days.length === 1) {
          daysLabel = group.days[0];
        } else if (group.days.length === 2) {
          daysLabel = `${group.days[0]} e ${group.days[1]}`;
        } else {
          daysLabel = `${group.days[0]} a ${group.days[group.days.length - 1]}`;
        }
        
        return (
          <div key={index} className="hours-group">
            <strong>{daysLabel}:</strong> {group.hours}
          </div>
        );
      });
    }

    // Fallback para estrutura antiga (dayGroups)
    if (businessHours.dayGroups && businessHours.dayGroups.length > 0) {
      return businessHours.dayGroups.map((group, index) => (
        <div key={index} className="hours-group">
          <strong>{group.days}:</strong> {group.hours}
        </div>
      ));
    }

    return <div className="hours-group">Horário não informado</div>;
  };

  if (loading) {
    return (
      <div className="place-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando informações do estabelecimento...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="place-page">
        <header className="place-header">
          <div className="container">
            <div className="header-content">
              <div className="logo">
                <img src="/img/logo-coffeein.png" alt="CoffeeIn" />
              </div>
              <button className="back-btn" onClick={() => navigate('/')}>
                ← Voltar
              </button>
            </div>
          </div>
        </header>
        <div className="error-container">
          <div className="error-content">
            <h2>Ops! Algo deu errado</h2>
            <p>{error}</p>
            <button className="retry-btn" onClick={() => window.location.reload()}>
              Tentar novamente
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!placeData) {
    return null;
  }

  return (
    <div className="place-page">
      {/* Header */}
      <header className="place-header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <img src="/img/logo-coffeein.png" alt="CoffeeIn" />
            </div>
            <button className="back-btn" onClick={() => navigate('/')}>
              ← Voltar
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="place-hero">
        <div className="container">
          <div className="hero-content">
            <div className="place-info">
              <h1 className="place-name">{placeData.name}</h1>
              <p className="place-description">{placeData.description || 'Sem descrição disponível'}</p>
              <div className="place-details">
                <div className="detail-item">
                  <span className="icon">📍</span>
                  <span>{placeData.address}</span>
                </div>
                {placeData.averageRating > 0 && (
                  <div className="detail-item">
                    <span className="icon">⭐</span>
                    <span>{placeData.averageRating.toFixed(1)}/5.0 ({placeData.reviewCount} avaliações)</span>
                  </div>
                )}
              </div>
              
              {/* Horário de funcionamento */}
              {placeData.businessHours && (
                <div className="business-hours">
                  <h3>Horário de Funcionamento</h3>
                  <div className="hours-list">
                    {formatBusinessHours(placeData.businessHours)}
                  </div>
                </div>
              )}

              {/* Instagram */}
              {placeData.instagram && (
                <div className="social-links">
                  <a 
                    href={`https://instagram.com/${placeData.instagram.replace('@', '')}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="instagram-link"
                  >
                    <span className="icon">📷</span>
                    @{placeData.instagram.replace('@', '')}
                  </a>
                </div>
              )}
            </div>
            <div className="place-image">
              {placeData.coverImage ? (
                <img 
                  src={placeData.coverImage} 
                  alt={placeData.name}
                  onError={(e) => {
                    e.target.src = '/img/portfolio/01-large.jpg';
                  }}
                  loading="lazy"
                />
              ) : (
                <img 
                  src="/img/portfolio/01-large.jpg" 
                  alt={placeData.name}
                />
              )}
            </div>
          </div>
        </div>
      </section>

      {/* App Download Section */}
      <section className="app-download">
        <div className="container">
          <div className="download-content">
            <h2>Baixe o app CoffeeIn</h2>
            <div className="download-buttons">
              {deviceType === 'ios' && (
                <a 
                  href={APP_STORE_URLS.ios}
                  className="download-btn ios-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/img/GetAtAppStore.svg" alt="Baixar na App Store" />
                </a>
              )}
              
              {deviceType === 'android' && (
                <a 
                  href={APP_STORE_URLS.android}
                  className="download-btn android-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src="/img/GetItOnGooglePlay.svg" alt="Disponível no Google Play" />
                </a>
              )}
              
              {deviceType === 'desktop' && (
                <div className="desktop-options">
                  <p>Escolha sua plataforma:</p>
                  <div className="platform-buttons">
                    <a 
                      href={APP_STORE_URLS.ios}
                      className="download-btn ios-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src="/img/GetAtAppStore.svg" alt="Baixar na App Store" />
                    </a>
                    <a 
                      href={APP_STORE_URLS.android}
                      className="download-btn android-btn"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img src="/img/GetItOnGooglePlay.svg" alt="Disponível no Google Play" />
                    </a>
                  </div>
                </div>
              )}
            </div>

            <div className="try-app-section">
              <p className="try-app-text">
                Já tem o app? 
                <button className="try-app-btn" onClick={handleTryApp}>
                  Abrir no app
                </button>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <div className="container">
          <h2>O que você pode fazer no app</h2>
          <div className="features-grid">
            <div className="feature">
              <div className="feature-icon">💰</div>
              <h3>Descontos Exclusivos</h3>
              <p>Aproveite ofertas especiais e economize em suas visitas.</p>
            </div>
            <div className="feature">
              <div className="feature-icon">⭐</div>
              <h3>Avaliações</h3>
              <p>Deixe sua opinião e veja o que outros clientes acham.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="place-footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src="/img/logo-coffeein.png" alt="CoffeeIn" />
              <span>CoffeeIn</span>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 CoffeeIn Club. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PlacePage;
